import { navigate, RouteComponentProps } from '@gatsbyjs/reach-router';
import styled, { useTheme } from 'styled-components';
import React, { useEffect, useMemo, useState } from 'react';
import EmptyStateComponent from '~/components/template/EmptyStateComponent';
import AppDetailsContainer from '~/components/page/Apps/components/AppDetailsContainer';
import TEST_ID from './index.testid';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import {
  AppStatusRealworksFieldsFragment,
  Exact,
  GetAppStatusRealworksQuery,
  RealworksRelatieSyncType,
  RealworksTokenStatus,
  useGetAppStatusRealworks_RealworksKenmerkenQuery,
} from '~/graphql/types';
import { Heading4, Body, Variant } from '~/components/atom/Typography';
import type { Kenmerk, KenmerkLookup } from './types';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { equals, isEmpty, isNil } from 'ramda';
import TextButton from '~/components/atom/TextButton';
import { animated, useSpring } from 'react-spring';
import { OperationVariables, WatchQueryOptions } from '@apollo/client';
import CaretDropdownButton from '~/components/molecule/CaretDropdownButton';
import { isValid } from '~/util/Validation/Tag';
import RadioButton from '~/components/molecule/RadioButton';
import { ANIMATION_CONFIG } from '~/styles/constants';
import Catalog from '~/Catalog';
import NoKenmerken from './components/NoKenmerken';
import Checkbox from '~/components/molecule/Checkbox';
import createKenmerkLookup from './util/createKenmerkLookup';
import RealworksSelectableAccordion from '../RealworksSelectableAccordion';
import generateMetadataForSyncType from './util/generateMetadataForSyncType';
import { assertNever } from '~/util/assertNever';
import hasValue from '~/util/hasValue';
import useErrorReporter from '~/hooks/useErrorReporter';
import Button from '~/components/atom/Button';
import useTooltipLayer from '~/hooks/useTooltipLayer';
import KenmerkSearchInput from './components/KenmerkSearchInput';
import Loading from '~/components/atom/Loading';
import useKenmerkSearch from './hooks/useKenmerkSearch';
import useSyncMutation from './hooks/useSyncMutation';

const text = {
  header: 'Synchronisatie',
  emptyState: {
    header: (
      <>
        Je hebt nog geen actieve koppelingen
        <br /> met Realworks.
      </>
    ),
    button: 'Toevoegen',
  },
  description: `Synchroniseer relaties uit Realworks als contacten in ${Catalog.companyName}. Je bepaalt welke relaties worden gesynchroniseerd en kunt kenmerken uit Realworks automatisch omzetten naar een tag in ${Catalog.companyName}.`,
  blockMarketingHeading: 'In/uitschrijving voor marketingmailings',
  syncInfoHeading: 'Welke relaties synchroniseren?',
  infoAboutBlockMarketing: `Relaties die in Realworks worden in- of uitgeschreven krijgen in BNDL automatisch dezelfde status. Wijzigingen in BNDL worden niet aan Realworks doorgegeven. Bij de eerste synchronisatie blijft de uitgeschreven status behouden die contacten hebben in BNDL. Daarna geldt: als zij in Realworks worden ingeschreven, worden zij ook in BNDL ingeschreven.`,
  infoAboutAddContactsWithTags: `Kies hieronder welke relaties uit Realworks als contact in BNDL worden aangemaakt. Bestaande contacten worden gekoppeld op basis van e-mailadres — er worden geen dubbele contacten aangemaakt. Kies op basis van welke relatiekenmerken je relaties uit Realworks wil synchroniseren aan een tag in BNDL. Tags worden alleen toegevoegd, nooit verwijderd.`,
  syncOptions: {
    full: 'Alle relaties',
    limited: 'Alleen relaties met geselecteerde kenmerken',
    excluding: 'Alle relaties, behalve relaties met geselecteerde kenmerken',
  },
  selectionDescription: 'Kenmerken en tags',
  searchPlaceholder: 'Zoek op kenmerk...',
  collapseOpenGroup: 'Groep inklappen',
  buttons: {
    deactivate: 'Deactiveer synchronisatie',
    save: 'Opslaan',
    saveAndActivate: 'Opslaan en activeren',
    activate: 'Activeren',
    saveAndDeactivate: 'Opslaan en deactiveren',
  },
  toasts: {
    error: {
      deactivate:
        'Er is iets fout gegaan bij het deactiveren van de synchronisatie, probeer het later opnieuw',
      sync: 'Er is iets fout gegaan bij het synchroniseren, probeer het later opnieuw',
      saveDeactivate: (isRelatieSyncEnabled: boolean) =>
        `Er is iets fout gegaan bij het ${isRelatieSyncEnabled ? 'opslaan en deactiveren van de synchronisatie' : 'opslaan van de wijzigingen'}, probeer het later opnieuw`,
    },

    success: {
      deactivate: 'Synchronisatie gedeactiveerd',
      activate: 'Synchronisatie geactiveerd',
      save: 'Wijzigingen opgeslagen',
    },
  },
  includeRelatiesWithoutKenmerken:
    'Ook relaties zonder kenmerken synchroniseren',
};

type Props = {
  appStatus: AppStatusRealworksFieldsFragment;
  updateQuery: <
    TVars extends OperationVariables = Exact<{ accountId: string }>,
  >(
    mapFn: (
      previousQueryResult: GetAppStatusRealworksQuery,
      options: Pick<
        WatchQueryOptions<TVars, GetAppStatusRealworksQuery>,
        'variables'
      >,
    ) => GetAppStatusRealworksQuery,
  ) => void;
} & RouteComponentProps;

const Synchronization: React.FCC<Props> = ({ appStatus, updateQuery }) => {
  const theme = useTheme();
  const { id: accountId } = useCurrentAccount();
  const reportError = useErrorReporter();

  const hasActiveRelatieToken = appStatus.tokens.some(
    token => token.relatie?.status === RealworksTokenStatus.Active,
  );

  const [hasValidationError, setHasValidationError] = useState(false);

  const initialSyncType = ((
    typename:
      | 'AppStatus_Realworks_FullSync'
      | 'AppStatus_Realworks_LimitToKenmerkenSync'
      | 'AppStatus_Realworks_FullExcludingKenmerkenSync'
      | null
      | undefined,
  ): RealworksRelatieSyncType => {
    if (isNil(typename)) {
      // noop but still good to know. It means the migration has failed for this account.
      reportError.captureException(
        new Error('syncType cannot be null or undefined'),
      );
      throw new Error('syncType cannot be null or undefined');
    }
    switch (typename) {
      case 'AppStatus_Realworks_FullSync':
        return RealworksRelatieSyncType.Full;
      case 'AppStatus_Realworks_LimitToKenmerkenSync':
        return RealworksRelatieSyncType.LimitToKenmerken;
      case 'AppStatus_Realworks_FullExcludingKenmerkenSync':
        return RealworksRelatieSyncType.FullExcludingKenmerken;
      default:
        return assertNever(typename, 'syncTypeToRealworksRelatieSyncType');
    }
  })(appStatus.relatieSync.syncMetadata?.__typename);

  const syncMetadata = appStatus.relatieSync.syncMetadata;

  const initialRelatiesWithoutKenmerken = useMemo(() => {
    if (
      syncMetadata?.__typename !== 'AppStatus_Realworks_LimitToKenmerkenSync'
    ) {
      return undefined;
    }
    return {
      include: syncMetadata.relatiesWithoutKenmerken.include,
    };
  }, [syncMetadata]);

  const [syncType, setSyncType] =
    useState<RealworksRelatieSyncType>(initialSyncType);
  const [relatiesWithoutKenmerken, setRelatiesWithoutKenmerken] = useState<{
    include: boolean;
  }>(initialRelatiesWithoutKenmerken ?? { include: false });

  const isRelatieSyncEnabled = appStatus.relatieSync.enabled;

  const styles = useSpring({
    from: {
      transform: isRelatieSyncEnabled ? 'translateX(-40px)' : 'translateX(0px)',
      opacity: isRelatieSyncEnabled ? 0 : 1,
    },
    to: {
      transform: isRelatieSyncEnabled ? 'translateX(0px)' : 'translateX(-40px)',
      opacity: isRelatieSyncEnabled ? 1 : 0,
    },
    config: ANIMATION_CONFIG.config,
  });

  const [initialKenmerkLookup, setInitialKenmerkLookup] =
    useState<KenmerkLookup | null>(null);
  const [updatedKenmerkLookup, setUpdatedKenmerkLookup] =
    useState<KenmerkLookup | null>(null);

  const { data, loading } = useGetAppStatusRealworks_RealworksKenmerkenQuery({
    variables: { accountId },
    skip: !hasActiveRelatieToken,
  });

  const realworksKenmerken = data?.getAppStatusRealworks_RealworksKenmerken;
  const kenmerkMapping = appStatus?.relatieSync?.syncMetadata?.kenmerkMapping;

  useEffect(() => {
    if (realworksKenmerken) {
      const initialLookup = createKenmerkLookup({
        kenmerkMapping,
        realworksKenmerken,
      });
      setInitialKenmerkLookup(initialLookup);
      setUpdatedKenmerkLookup(initialLookup);
    }
  }, [kenmerkMapping, realworksKenmerken]);

  const { onUpdate, updating } = useSyncMutation(
    updatedKenmerkLookup,
    updateQuery,
  );

  const hasChanges = useMemo(() => {
    const original = {
      ...initialKenmerkLookup,
      syncType: initialSyncType,
      ...(initialSyncType === RealworksRelatieSyncType.LimitToKenmerken && {
        relatiesWithoutKenmerken: initialRelatiesWithoutKenmerken ?? {
          include: false,
        },
      }),
    };
    const updated = {
      ...updatedKenmerkLookup,
      syncType,
      ...(syncType === RealworksRelatieSyncType.LimitToKenmerken && {
        relatiesWithoutKenmerken,
      }),
    };

    return !equals(original, updated);
  }, [
    initialKenmerkLookup,
    initialSyncType,
    initialRelatiesWithoutKenmerken,
    updatedKenmerkLookup,
    syncType,
    relatiesWithoutKenmerken,
  ]);

  const {
    openGroupName,
    setOpenGroupName,
    activeKenmerkSearch,
    kenmerkSearchNavigation,
    handleSearch: handleKenmerkSearch,
    handlePreviousMatch: handlePreviousKenmerkMatch,
    handleNextMatch: handleNextKenmerkMatch,
    handleCollapseOpenGroup,
  } = useKenmerkSearch(updatedKenmerkLookup);

  const collapseOpenGroupTooltipProps = useTooltipLayer({
    tooltipMessage: text.collapseOpenGroup,
  });

  const onChange = ({
    groupName,
    kenmerkList,
  }: {
    groupName: string;
    kenmerkList: Array<Kenmerk>;
  }) => {
    setHasValidationError(
      kenmerkList.some(
        // Only validate when tag is not null and empty
        ({ tag }) => !isNil(tag) && !isEmpty(tag) && !isValid(tag),
      ),
    );
    setUpdatedKenmerkLookup(prevState => ({
      ...prevState,
      [groupName]: kenmerkList,
    }));
  };

  if (loading) {
    return <Loading />;
  }

  if (!data || appStatus?.enabled === false)
    return (
      <AppDetailsContainer
        header={text.header}
        icon="reload"
        loading={updating}
        dataTestId={TEST_ID.CONTAINER}
      >
        <EmptyStateComponent
          header={text.emptyState.header}
          illustration="box"
          buttonProps={{
            label: text.emptyState.button,
            onClick: () =>
              navigate('/-/apps/realworks/wizard/', { replace: true }),
          }}
          blobColor={theme.color('tertiary', 'light')}
        />
      </AppDetailsContainer>
    );

  if (!realworksKenmerken || !hasValue(updatedKenmerkLookup)) {
    return (
      <AppDetailsContainer
        header={text.header}
        loading={false}
        dataTestId={TEST_ID.CONTAINER}
      >
        <NoKenmerken
          isSyncEnabled={isRelatieSyncEnabled}
          updateQuery={updateQuery}
        />
      </AppDetailsContainer>
    );
  }

  const mainButtonLabel = (() => {
    if (hasChanges) {
      if (isRelatieSyncEnabled) return text.buttons.save;
      return text.buttons.saveAndActivate;
    } else {
      if (!isRelatieSyncEnabled) return text.buttons.activate;
      return text.buttons.save;
    }
  })();

  return (
    <AppDetailsContainer
      header={text.header}
      loading={false}
      dataTestId={TEST_ID.CONTAINER}
    >
      <JustificationContainer direction="column" gap="l" width="100%">
        <Body margin={[null]}>{text.description}</Body>
        <JustificationContainer direction="column" gap="xs" width="100%">
          <Heading4 margin={[null]} variant={Variant.primary}>
            {text.blockMarketingHeading}
          </Heading4>
          <Body margin={[null]}>{text.infoAboutBlockMarketing}</Body>
        </JustificationContainer>

        <JustificationContainer direction="column" gap="xs" width="100%">
          <Heading4 margin={[null]} variant={Variant.primary}>
            {text.syncInfoHeading}
          </Heading4>
          <Body margin={[null]}>{text.infoAboutAddContactsWithTags}</Body>
        </JustificationContainer>

        <JustificationContainer
          direction="column"
          gap="base"
          margin={[null, null, 'l', null]}
        >
          <RadioButton
            checked={syncType === RealworksRelatieSyncType.Full}
            onChange={() => setSyncType(RealworksRelatieSyncType.Full)}
            label={text.syncOptions.full}
            dataTestId={TEST_ID.FULL_OPTION}
          />
          <RadioButton
            checked={syncType === RealworksRelatieSyncType.LimitToKenmerken}
            onChange={() =>
              setSyncType(RealworksRelatieSyncType.LimitToKenmerken)
            }
            label={text.syncOptions.limited}
            dataTestId={TEST_ID.LIMITED_TO_KENMERKEN_OPTION}
          />
          {syncType === RealworksRelatieSyncType.LimitToKenmerken && (
            <JustificationContainer
              direction="column"
              gap="base"
              margin={[null, null, null, 'l']}
            >
              <Checkbox
                value={relatiesWithoutKenmerken.include}
                onChange={() =>
                  setRelatiesWithoutKenmerken(prev => ({
                    ...prev,
                    include: !prev.include,
                  }))
                }
                label={text.includeRelatiesWithoutKenmerken}
              />
            </JustificationContainer>
          )}
          <RadioButton
            checked={
              syncType === RealworksRelatieSyncType.FullExcludingKenmerken
            }
            onChange={() =>
              setSyncType(RealworksRelatieSyncType.FullExcludingKenmerken)
            }
            label={text.syncOptions.excluding}
            dataTestId={TEST_ID.EXCLUDING_KENMERKEN_OPTION}
          />
        </JustificationContainer>
      </JustificationContainer>
      <JustificationContainer direction="column" gap="base" width="100%">
        <JustificationContainer
          width="100%"
          justification="space-between"
          align="center"
        >
          <Heading4 margin={[null]} variant={Variant.primary}>
            {text.selectionDescription}
          </Heading4>
          <JustificationContainer
            direction="row"
            gap="s"
            align="center"
            width="50%"
          >
            <SearchInputWrapper>
              <KenmerkSearchInput
                onQueryChange={handleKenmerkSearch}
                placeholder={text.searchPlaceholder}
                width="100%"
                hasQuery={Boolean(kenmerkSearchNavigation?.query?.trim())}
                matchCount={kenmerkSearchNavigation?.matches.length ?? 0}
                currentMatchIndex={kenmerkSearchNavigation?.currentIndex ?? 0}
                onPreviousMatch={handlePreviousKenmerkMatch}
                onNextMatch={handleNextKenmerkMatch}
              />
            </SearchInputWrapper>
            <Button
              {...collapseOpenGroupTooltipProps}
              icon="chevron-up"
              ghost
              appearance="tertiary"
              size="small"
              disabled={!openGroupName}
              onClick={handleCollapseOpenGroup}
              dataTestId={TEST_ID.COLLAPSE_GROUP_BUTTON}
            />
          </JustificationContainer>
        </JustificationContainer>
        <RealworksSelectableAccordion
          kenmerkLookup={updatedKenmerkLookup}
          onChange={onChange}
          openGroupName={openGroupName}
          onOpenGroupChange={setOpenGroupName}
          activeKenmerkSearch={activeKenmerkSearch}
          selectable={
            syncType === RealworksRelatieSyncType.LimitToKenmerken ||
            syncType === RealworksRelatieSyncType.FullExcludingKenmerken
          }
          syncType={syncType}
        />
      </JustificationContainer>

      <JustificationContainer
        justification={styles.opacity ? 'space-between' : 'end'}
        margin={['l', null, null]}
      >
        <TextButton
          label={text.buttons.deactivate}
          appearance="danger"
          disabled={updating}
          onClick={() =>
            onUpdate(
              {
                accountId,
                updateRelatieSync: {
                  enabled: false,
                  syncMetadata: generateMetadataForSyncType({
                    syncType,
                    updatedKenmerkLookup,
                    relatiesWithoutKenmerken,
                  }),
                },
              },
              {
                errorToast: text.toasts.error.deactivate,
                successToast: text.toasts.success.deactivate,
              },
            )
          }
          dataTestId={TEST_ID.DEACTIVATE_BUTTON}
          as={animated.button}
          style={{
            alignSelf: 'flex-end',
            ...(styles as unknown as React.CSSProperties),
          }}
        />

        <CaretDropdownButton
          mainButtonOption={{
            label: mainButtonLabel,
            onClickAction: () =>
              onUpdate(
                {
                  accountId,
                  updateRelatieSync: {
                    enabled: true,
                    syncMetadata: generateMetadataForSyncType({
                      syncType,
                      updatedKenmerkLookup,
                      relatiesWithoutKenmerken,
                    }),
                  },
                },
                {
                  errorToast: text.toasts.error.sync,
                  successToast: !isRelatieSyncEnabled
                    ? text.toasts.success.activate
                    : text.toasts.success.save,
                },
              ),
          }}
          dropdownOptions={[
            {
              label: isRelatieSyncEnabled
                ? text.buttons.saveAndDeactivate
                : text.buttons.save,
              onClickAction: () =>
                onUpdate(
                  {
                    accountId,
                    updateRelatieSync: {
                      enabled: false,
                      syncMetadata: generateMetadataForSyncType({
                        syncType,
                        updatedKenmerkLookup,
                        relatiesWithoutKenmerken,
                      }),
                    },
                  },
                  {
                    errorToast:
                      text.toasts.error.saveDeactivate(isRelatieSyncEnabled),
                    successToast: isRelatieSyncEnabled
                      ? text.toasts.success.deactivate
                      : text.toasts.success.save,
                  },
                ),
              type: !hasChanges ? 'DISABLED' : undefined,
            },
          ]}
          dataTestId={TEST_ID.SAVE_BUTTON}
          loading={updating}
          disabled={(!hasChanges && isRelatieSyncEnabled) || hasValidationError}
        />
      </JustificationContainer>
    </AppDetailsContainer>
  );
};

const SearchInputWrapper = styled.div<{}>`
  flex: 1;
  min-width: 0;
`;

export default Synchronization;
